/**
 * ToolDetail.tsx — the /tools/:toolId surface. Four zones, in the fixed order
 * `REQUIREMENTS_TOOL_DETAIL_PAGE.md` §4/§6 requires — Decide, Trust, Act, Learn from others — plus
 * the ADOPT interaction (Stage 3.5, unchanged below): clone the tool's stored blueprint into the
 * adopter's approved Power Automate environment, producing a new Draft/Off flow.
 *
 * THE READS (loader): GET /api/tools/:toolId (the detail, now carrying the full trust-signal set)
 * and GET /api/tools/:toolId/comments (the first page), fetched in parallel — plus GET /api/flows/mine
 * for the adopt selector's environment, exactly as before.
 *
 * THE WRITES: POST /api/tools/:toolId/adopt (unchanged) plus the new feedback actions — like/save,
 * comment/edit/delete/builder-remove, rating, issue report + resolve, the did-it-work prompt.
 *
 * TRUST MODEL: unchanged from Stage 3.5 for adopt (see AdoptPanel below). The feedback actions follow
 * the same posture: the browser sends only what it is asked for; the actor is always the session.
 */
import { useState, useEffect } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router';
import { AVATAR_DATA_URI_RE, numberComponents } from '@trailblazer/shared';
import type { FlowComponent, FlowComponentDoc } from '@trailblazer/shared';
import {
  apiGet,
  powerAutomateDeepLink,
  type Handover,
  type HandoverMode,
  likeTool,
  unlikeTool,
  saveTool,
  unsaveTool,
  fetchComments,
  postComment,
  editComment,
  deleteComment,
  removeCommentByBuilder,
  reportComment,
  postRating,
  postIssueReport,
  resolveIssueReport,
  postDidItWork,
  setToolHealth,
  editToolClassification,
  withdrawTool,
  restoreTool,
  mergeTools,
  reassignToolOwner,
  fetchAdminDuplicateCandidates,
  adoptTool,
  fetchToolAudit,
  type AdoptResult,
  type AdoptBlockedReason,
  type CatalogueToolDetail,
  type FlowsMineResponse,
  type CommentNode,
  type CommentsPage,
  type RefOption,
  type ToolRefData,
  type AdminDuplicateCandidate,
  type ToolAuditEntry,
} from './api';
// DS-NFR06: the ONE wording for every trust signal a card shows too. See trustSignals.ts's header for
// which signals are there, which stay inline in TrustZone below, and why.
import {
  TRUST_SIGNAL_LABELS,
  shortDate,
  formatAdoption,
  formatReviewMarker,
  formatConfirmedWorking,
  formatHealth,
  formatRating,
  formatOpenIssueCount,
  openIssuesDisplay,
} from './trustSignals';
// TD-F04: the same two href builders the catalogue's result cards use.
import { catalogueCategoryHref, catalogueTagHref } from './catalogueLinks';
import { fetchSuggestions } from '../search/api';
import { STOCK_AVATARS, monogram } from '../Profile';
import { FeatureDisabled, SignedOut, TOOLS_STYLES } from './shared';
import { recordToolClick } from './recentlyClickedStore';

/** The approved environment the adopt selector offers, when it could be resolved. */
interface EnvOption {
  id: string;
  name: string;
}

export type DetailLoaderData =
  | { state: 'signedOut' }
  | { state: 'featureDisabled' }
  | { state: 'notFound' }
  | {
      state: 'ok';
      tool: CatalogueToolDetail;
      comments: CommentsPage;
      env: EnvOption | null;
      envNote: string | null;
      /** TD-F23: gates the owner-actions panel. Resolved from the caller's own live roles
       *  (/api/me), the same field App.tsx's nav already reads — never trusted from anywhere else. */
      isPlatformOwner: boolean;
    };

/**
 * Load the tool detail and its first page of comments IN PARALLEL (true concurrency, unlike the
 * sequential env resolution below, which genuinely depends on nothing from the detail either but is
 * left as a second step to keep this diff's risk to the untouched adopt path minimal). A 404 that
 * carries `tool_not_found` means the tool is absent (feature is on); a bare 404 means the Dataverse
 * route family is not mounted (feature off).
 */
export async function toolDetailLoader({
  params,
}: {
  params: { toolId?: string };
}): Promise<DetailLoaderData> {
  const toolId = params.toolId ?? '';

  const [detailRes, commentsRes, meRes] = await Promise.all([
    apiGet<CatalogueToolDetail>(`/api/tools/${encodeURIComponent(toolId)}`),
    fetchComments(toolId),
    fetch('/api/me', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
  ]);
  if (detailRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (detailRes.kind === 'error') {
    if (detailRes.status === 404 && detailRes.code === 'tool_not_found')
      return { state: 'notFound' };
    if (detailRes.status === 404) return { state: 'featureDisabled' }; // route not mounted
    throw new Error(`GET /api/tools/:toolId failed (${detailRes.status})`);
  }
  const comments: CommentsPage =
    commentsRes.kind === 'ok' ? commentsRes.body : { comments: [], total: 0 };

  // Resolve the approved environment for the adopt selector. Failures here don't block the detail.
  const flowsRes = await apiGet<FlowsMineResponse>('/api/flows/mine');
  let env: EnvOption | null = null;
  let envNote: string | null = null;
  if (flowsRes.kind === 'ok') {
    env = { id: flowsRes.body.environment_id, name: flowsRes.body.environment_name };
  } else if (flowsRes.kind === 'error' && flowsRes.status === 409) {
    envNote = 'Sign out and sign back in so the Dataverse permission is granted, then retry adopt.';
  } else if (flowsRes.kind === 'error' && flowsRes.code === 'no_approved_environment') {
    envNote = 'No approved Power Automate environment is configured, so adoption is unavailable.';
  } else if (flowsRes.kind === 'error') {
    envNote = `Could not resolve the target environment (${flowsRes.status}${flowsRes.code ? `: ${flowsRes.code}` : ''}).`;
  }

  // DEMO MODE (API config.DEMO_MODE_ADOPT, ADR-008 ruling 2). /api/flows/mine cannot answer without a
  // Dataverse tenant, so with none in the room every branch above leaves `env` null, the panel below
  // never renders, and there is no Adopt button to press at all. When the API is running in demo mode
  // it serves an environment alongside the tool itself; fall back to that.
  //
  // AND SAY SO — replacing whichever failure note we just wrote, because the environment did resolve,
  // just not from Dataverse. The note is not optional and not cosmetic: an adoption recorded this way
  // creates NO flow in Power Automate, and someone who clicks Adopt is otherwise going to go looking
  // for a flow that was never made. Demo mode is fenced off in production at the API, so this branch
  // is unreachable there and the wording never faces a real user.
  const demoTarget = detailRes.body.demoAdoptTarget;
  if (env === null && demoTarget) {
    env = { id: demoTarget.environmentId, name: demoTarget.environmentName };
    envNote =
      `Demo mode: adopting records the adoption against ${demoTarget.environmentName}, but no flow ` +
      `is created in Power Automate and nothing will appear in your environment.`;
  }

  // A failure here never blocks the detail page — it just means the owner-actions panel stays
  // hidden (the safe default), same posture as the env resolution above.
  let isPlatformOwner = false;
  if (meRes.ok) {
    const me = (await meRes.json()) as { roles?: string[] };
    isPlatformOwner = (me.roles ?? []).includes('PlatformOwner');
  }

  return { state: 'ok', tool: detailRes.body, comments, env, envNote, isPlatformOwner };
}

/** Render the connector requirements list honestly. */
function connectorList(connectors: { apiName: string }[]): string {
  return connectors.length === 0 ? 'None listed' : connectors.map((c) => c.apiName).join(', ');
}

/**
 * "Description of connectors" — a collapsible disclosure that reveals the builder's mandatory per-step
 * setup descriptions (0020 component docs). Filling these in is REQUIRED to publish a tool, but until
 * now they surfaced nowhere on the tool page; an adopter can read them here to see how each step /
 * connector is configured before adopting. READ-ONLY — this only displays the saved record; the step
 * scaffolding (number, branch, label, connector) is machine-derived and the explanation is the
 * builder's own prose. Numbering is derived on demand with `numberComponents` (docs store none), the
 * same way the flattened setup text is rendered, so it matches the upload wizard exactly.
 */
function ConnectorDescriptions({ steps }: { steps: FlowComponentDoc[] }) {
  // Docs carry no `numbering` (it is a function of the current structure); derive it the same way
  // renderComponentDocsAsText does, so what an adopter reads matches the upload wizard's numbering.
  // numberComponents preserves input order, so we pair each numbered result back with its source doc
  // (by index) to recover the `explanation`, which FlowComponent does not carry.
  const numbering = numberComponents(
    steps.map((s) => ({ ...s, numbering: '' })) as FlowComponent[],
  ).map((c) => c.numbering);
  return (
    <details className="tb-connector-desc">
      <summary>
        <strong>Description of connectors</strong>
        <span className="tb-muted"> — how the original builder set up each step</span>
      </summary>
      <ol className="tb-connector-steps">
        {steps.map((c, i) => (
          <li
            key={c.id}
            className="tb-connector-step"
            style={{ marginLeft: `${Math.min(c.depth, 5) * 0.9}rem` }}
          >
            <div className="tb-connector-step__head">
              <span className="tb-connector-step__num">{numbering[i]}</span>
              {c.branch ? <span className="tb-connector-step__branch">{c.branch}</span> : null}
              <span className="tb-connector-step__label">{c.label}</span>
            </div>
            {c.typeLabel || c.connector || c.operation ? (
              <div className="tb-connector-step__meta">
                {[c.typeLabel, c.connector, c.operation].filter(Boolean).join(' · ')}
              </div>
            ) : null}
            <p className="tb-connector-step__body">{c.explanation.trim()}</p>
          </li>
        ))}
      </ol>
    </details>
  );
}

/** Format an ISO timestamp as a local date + time, for comment/issue timestamps. */
function shortDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * TD-F15: a plain-words reason to warn about before adopting, or null when the tool is Healthy with
 * no open issues. Combines both conditions into one sentence when both apply, so the warning and the
 * AdoptPanel confirmation gate always agree on the same wording.
 */
function safetyWarning(tool: CatalogueToolDetail): string | null {
  const parts: string[] = [];
  if (tool.healthState !== 'Healthy') parts.push(`marked ${tool.healthState}`);
  // The COUNT, not `openIssues.length`: the bodies are withheld from everyone but the builder, the
  // platform owner and the SWAT team (TD-F20), and a safety warning that fell silent for everyone else
  // would be worst exactly where it matters — the ordinary colleague about to adopt. Worded by
  // `formatOpenIssueCount` so this sentence and the Trust list cannot disagree (DS-NFR06).
  const openIssues = formatOpenIssueCount(tool.openIssueCount);
  if (openIssues !== null) parts.push(`has ${openIssues}`);
  if (parts.length === 0) return null;
  return `This tool is ${parts.join(' and ')}.`;
}

// ── Builder credit (TD-F02) ─────────────────────────────────────────────────────────────────────

/**
 * Resolve the builder's avatar, mirroring PersonProfile.tsx's `avatarUrl` chokepoint exactly (same
 * allowlist, same regex) — plain text credit, not a link (TD-F02: "not a link to a full profile in
 * v1"), with a photo when one resolves and a monogram fallback otherwise.
 */
function builderAvatarUrl(tool: CatalogueToolDetail): string | null {
  if (tool.builderAvatarKind === 'stock' && typeof tool.builderAvatarRef === 'string') {
    const hit = STOCK_AVATARS.get(tool.builderAvatarRef.slice('stock:'.length));
    if (hit) return hit.url;
  }
  if (
    tool.builderAvatarKind === 'upload' &&
    typeof tool.builderAvatarRef === 'string' &&
    AVATAR_DATA_URI_RE.test(tool.builderAvatarRef)
  ) {
    return tool.builderAvatarRef;
  }
  if (typeof tool.builderPhotoRef === 'string' && AVATAR_DATA_URI_RE.test(tool.builderPhotoRef)) {
    return tool.builderPhotoRef;
  }
  return null;
}

function BuilderCredit({ tool }: { tool: CatalogueToolDetail }) {
  const img = builderAvatarUrl(tool);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.75rem 0' }}>
      {img ? (
        <img
          src={img}
          alt=""
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '1px solid var(--tb-line)',
          }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            background: 'var(--tb-accent)',
            color: 'var(--tb-accent-ink)',
          }}
        >
          {monogram(tool.ownerName)}
        </span>
      )}
      <div>
        <div>
          {tool.ownerName} {tool.mine ? '(you)' : ''}
        </div>
        {tool.builderDepartment ? <div className="tb-muted">{tool.builderDepartment}</div> : null}
        {tool.ownerlessFlag && (
          <div
            className="tb-note tb-note--warn"
            style={{ margin: '0.25rem 0 0', display: 'inline-block' }}
          >
            This tool's builder has left — it needs an owner.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Trust zone (TD §8, the ten signals in this exact order) ────────────────────────────────────

function StarRating({
  toolId,
  myRating,
  onRated,
}: {
  toolId: string;
  myRating: number | null;
  onRated: (stars: number) => void;
}) {
  const [saving, setSaving] = useState(false);
  async function rate(stars: number) {
    setSaving(true);
    const res = await postRating(toolId, stars);
    setSaving(false);
    if (res.kind === 'ok') onRated(stars);
  }
  return (
    <span role="group" aria-label="Rate this tool">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={saving}
          aria-pressed={myRating === n}
          onClick={() => void rate(n)}
          style={{
            background: 'transparent',
            border: 0,
            padding: '0 0.1rem',
            cursor: saving ? 'not-allowed' : 'pointer',
            color: myRating !== null && n <= myRating ? 'var(--tb-accent)' : 'var(--tb-muted)',
            fontSize: '1.1rem',
          }}
        >
          ★
        </button>
      ))}
      {myRating ? <span className="tb-muted"> Your rating: {myRating}/5</span> : null}
    </span>
  );
}

function TrustZone({
  tool,
  onRated,
}: {
  tool: CatalogueToolDetail;
  onRated: (stars: number) => void;
}) {
  // Both formatters answer "is there anything to say?" as well as "what does it say", so each value is
  // computed once and the JSX tests the value — not the number, a second time, with its own threshold.
  const confirmedWorking = formatConfirmedWorking(tool.recentlyConfirmedWorkingCount);
  const health = formatHealth(tool.healthState);
  // Signal 5 decides three things at once (say nothing / show the reports / show the count and why),
  // so it is one call here rather than three conditions in the JSX. See trustSignals.ts.
  const openIssues = openIssuesDisplay(tool);

  return (
    <section aria-labelledby="trust-heading">
      <h2 id="trust-heading">Trust</h2>
      {/*
        EVERY SIGNAL §8 ALSO GIVES A CARD IS WORDED BY `tools/trustSignals.ts`, not here. This list used
        to hold the only copy of that wording, so the catalogue card had nothing to share and DS-NFR06
        could only be met by someone remembering to copy a template string. The formatters return `null`
        for the signals §8 hides rather than shows-as-zero, so the conditions below read off the value
        instead of re-testing the number — one place decides both what is said and whether it is said.

        Signals 6, 8 and 9 are still worded inline further down: all three are "Card: no", so they have
        one surface and nothing to drift from.
      */}
      <dl>
        {/* Signal 1: adoption count — explicit absence copy (TD-F07), hence never conditional. */}
        <dt>{TRUST_SIGNAL_LABELS.adoption}</dt>
        <dd>{formatAdoption(tool)}</dd>

        {/* Signal 2: review marker — the FULL form (date + version); the card gets an icon. */}
        <dt>{TRUST_SIGNAL_LABELS.review}</dt>
        <dd>{formatReviewMarker(tool)}</dd>

        {/* Signal 3: recently confirmed working — the formatter returns null at zero (FB-F14). */}
        {confirmedWorking !== null && (
          <>
            <dt>{TRUST_SIGNAL_LABELS.confirmedWorking}</dt>
            <dd>{confirmedWorking}</dd>
          </>
        )}

        {/* Signal 4: health state — the formatter returns null when Healthy. */}
        {health !== null && (
          <>
            <dt>{TRUST_SIGNAL_LABELS.health}</dt>
            <dd>{health}</dd>
          </>
        )}

        {/* Signal 5: TD-F09 says the open reports are a visible signal; TD-F20 says the reports
            themselves are private to the builder, the platform owner and the SWAT team. So the COUNT is
            shown to everyone and the BODIES only to those three — the decision is `openIssuesDisplay`,
            never `openIssues.length`, which is 0 for every gated viewer and would render nothing at all
            on a tool that has open reports. */}
        {openIssues.kind !== 'none' && (
          <>
            <dt>{TRUST_SIGNAL_LABELS.openIssues}</dt>
            <dd>
              {openIssues.kind === 'bodies' ? (
                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                  {tool.openIssues.map((i) => (
                    <li key={i.issueId}>
                      <strong>{i.kind}</strong> — {i.body}{' '}
                      <span className="tb-muted">({shortDate(i.createdAt)})</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <>
                  {openIssues.count} <span className="tb-muted">({openIssues.reason})</span>
                </>
              )}
            </dd>
          </>
        )}

        {/* Signal 6: version + last updated. "Card: no" — one surface, so worded inline. */}
        <dt>Version</dt>
        <dd>
          {tool.version} <span className="tb-muted">(updated {shortDate(tool.lastUpdated)})</span>
        </dd>

        {/*
          Signal 7: access requirement — explicit absence copy when 'all'. "Card: yes (if unmet)", and
          still worded inline: the API's access gate is a WHERE on the catalogue query, so a tool whose
          requirement the viewer does not meet is never on the page to be carded. Unreachable there,
          one surface here.
        */}
        <dt>Access</dt>
        <dd>
          {tool.accessRuleKind === 'all' ? 'No special access needed' : tool.accessRequirementLabel}
        </dd>

        {/* Signal 8: estimated time saved. "Card: no" — one surface. */}
        <dt>Time saved per use</dt>
        <dd>{tool.estimatedTimeSavedPerUse} minutes (as estimated by the builder)</dd>

        {/* Signal 9: like count — nothing shown when zero. "Card: no", and enforced by the payload:
            the catalogue list does not carry likeCount, so no card can render it. */}
        {tool.likeCount > 0 && (
          <>
            <dt>Likes</dt>
            <dd>{tool.likeCount}</dd>
          </>
        )}

        {/* Signal 10: average rating — shared wording; the rate control only for an adopter (TD-F26). */}
        <dt>{TRUST_SIGNAL_LABELS.rating}</dt>
        <dd>
          {formatRating(tool)}
          {tool.adoptedByMe && !tool.mine && (
            <div style={{ marginTop: '0.25rem' }}>
              <StarRating toolId={tool.toolId} myRating={tool.myRating} onRated={onRated} />
            </div>
          )}
        </dd>
      </dl>
    </section>
  );
}

// ── Act zone: like/save alongside the unchanged AdoptPanel (FB-F01..F03, TD-F13) ──────────────────

function LikeSaveActions({ tool }: { tool: CatalogueToolDetail }) {
  const [liked, setLiked] = useState(tool.likedByMe);
  const [likeCount, setLikeCount] = useState(tool.likeCount);
  const [saved, setSaved] = useState(tool.savedByMe);
  const [busy, setBusy] = useState(false);

  /**
   * Every handler below is wrapped in try/finally: `busy` MUST reset even when the fetch itself
   * throws (a network failure, e.g. the API being briefly unreachable) — without it a single failed
   * click leaves the button permanently disabled until the page is reloaded.
   */
  async function toggleLike() {
    setBusy(true);
    try {
      if (liked) {
        await unlikeTool(tool.toolId);
        setLiked(false);
        setLikeCount((c) => Math.max(0, c - 1));
      } else {
        const res = await likeTool(tool.toolId);
        if (res.kind === 'ok') {
          setLiked(true);
          if (res.body.outcome === 'liked') setLikeCount((c) => c + 1);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  async function toggleSave() {
    setBusy(true);
    try {
      if (saved) {
        await unsaveTool(tool.toolId);
        setSaved(false);
      } else {
        const res = await saveTool(tool.toolId);
        if (res.kind === 'ok') setSaved(true);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', margin: '0.75rem 0' }}>
      <button
        type="button"
        onClick={() => void toggleLike()}
        disabled={busy || tool.mine}
        title={tool.mine ? "You can't like your own tool" : undefined}
      >
        {liked ? '♥ Liked' : '♡ Like'} {likeCount > 0 ? `(${likeCount})` : ''}
      </button>
      <button type="button" onClick={() => void toggleSave()} disabled={busy}>
        {saved ? '★ Saved' : '☆ Save'}
      </button>
      {saved && (
        <span className="tb-private__marker" style={{ marginBottom: 0 }}>
          Private — only you can see this
        </span>
      )}
    </div>
  );
}

/**
 * The success panel. AE-F06 asks it to name three things — WHAT was adopted, WHOSE tool it is, and
 * WHERE TO FIND IT AGAIN — and it did none of them: the old heading was the single word "Adopted".
 * All three now come from the RESPONSE rather than the loaded page, so the confirmation describes the
 * tool as it was at the moment of adoption even if the page has since gone stale.
 *
 * Three things it deliberately REFUSES to say:
 *
 *   - A Power Automate link in demo mode. No flow was created, so the link leads nowhere and the
 *     person goes looking for a copy that does not exist — the most confusing possible outcome of a
 *     successful adopt.
 *   - An adoption total the API could not read (`adoption_count: null`). The row is omitted rather
 *     than filled in with the page's pre-adoption figure, which would be a wrong number presented as
 *     a new one.
 *   - That the adoption was counted when `adoptionRecorded` is false. The flow exists and the count
 *     does not; saying so turns a silently wrong total into a known, reportable gap.
 */
function AdoptedResult({ result }: { result: AdoptResult }) {
  // Composed client-side from the returned ids; the backend never emits this host.
  const deepLink = powerAutomateDeepLink(result.environment_id, result.new_workflow_id);
  return (
    <div className="tb-note tb-note--ok" role="status">
      <h3 style={{ marginTop: 0 }}>You have adopted {result.tool_name}</h3>
      <p style={{ marginTop: 0 }}>
        Built by <strong>{result.builder_name}</strong>.{' '}
        {result.demoMode
          ? 'Nothing was created in Power Automate — see the note below.'
          : 'Power Automate should have opened in a new tab. If it did not (pop-ups can be blocked), use the link below.'}
      </p>
      <dl>
        {result.adoption_count !== null && (
          <>
            <dt>Adopted by</dt>
            <dd>
              {result.adoption_count} {result.adoption_count === 1 ? 'person' : 'people'}
            </dd>
          </>
        )}
        {/* AE-F06's third element: somewhere to come back to, not just a link into Power Automate.
            The catalogue already understands ?scope=adopted, so this needs no new screen. */}
        <dt>Find it again</dt>
        <dd>
          <Link to="/tools?scope=adopted">Your adopted tools</Link>
        </dd>
        <dt>New workflow id</dt>
        <dd className="tb-mono">{result.new_workflow_id}</dd>
        <dt>Environment</dt>
        <dd>{result.environment_name}</dd>
        <dt>Connectors required</dt>
        <dd>{connectorList(result.requiredConnectors)}</dd>
        <dt>Connections to set up</dt>
        <dd>
          {result.missingConnections.length === 0 ? 'None' : result.missingConnections.join(', ')}
        </dd>
        {!result.demoMode && (
          <>
            <dt>Open in Power Automate</dt>
            <dd>
              <a href={deepLink} target="_blank" rel="noreferrer" className="tb-mono">
                {deepLink}
              </a>
            </dd>
          </>
        )}
      </dl>
      {!result.adoptionRecorded && (
        <p className="tb-note tb-note--warn" style={{ margin: '0.75rem 0 0' }}>
          The copy was created, but recording the adoption <strong>failed</strong> — it is not
          counted in the totals. Nothing is lost; the flow is in your environment. Tell the platform
          team so the count can be corrected.
        </p>
      )}
      {result.demoMode ? (
        <p className="tb-note tb-note--warn" style={{ margin: '0.75rem 0 0' }}>
          <strong>Demo mode.</strong> The adoption was recorded, but{' '}
          <strong>no flow was created</strong> in Power Automate — there is no Dataverse environment
          connected to this build. Nothing will appear in your environment, and the workflow id
          above is a placeholder, not a real flow.
        </p>
      ) : (
        <p className="tb-note tb-note--warn" style={{ margin: '0.75rem 0 0' }}>
          The copied flow opens as <strong>Draft / Off</strong>. Connector credentials are{' '}
          <strong>not</strong> copied — you must sign in to each missing connector and fix the
          connections in Power Automate before turning the flow on.
        </p>
      )}
    </div>
  );
}

/**
 * The adopt control + result panel. Shown only when the approved environment resolved.
 *
 * Two entry paths, chosen by `alreadyAdopted` (the viewer's OWN prior adoption, from
 * tool.adoptedByMe):
 *   • not yet adopted → a single "Adopt this tool" button that adopts immediately;
 *   • already adopted → a "You have already adopted this tool." message; clicking the button first
 *     asks "Do you want to create another copy in Power Automate?" (No, keep existing / Yes, adopt
 *     another copy) and only calls the backend on an explicit Yes.
 *
 * On any successful adopt we open Power Automate in a new tab from the ids the backend returned, and
 * ALSO render the link as a fallback (window.open can be blocked). The product word is "adopt"; the
 * repeat action is "Adopt another copy" (never "clone again").
 *
 * UNCHANGED from before this feature: this component's internals are not touched by the feedback work.
 */
function AdoptPanel({
  toolId,
  env,
  alreadyAdopted,
  safetyWarning,
  blocked,
}: {
  toolId: string;
  env: EnvOption;
  alreadyAdopted: boolean;
  /**
   * AE-F02 / RT-F07: non-null when this tool may not be adopted, carrying the reason to show. The
   * control stays VISIBLE and goes disabled rather than vanishing — "return the reason, never hide the
   * action". The endpoint enforces the same rule, so this is the courtesy, not the guard.
   */
  blocked: AdoptBlockedReason | null;
  /** TD-F15: non-null when the tool is broken or has an open issue — adopt requires an explicit
   * confirmation naming why, even on a FIRST adopt (not just a repeat one). */
  safetyWarning: string | null;
}) {
  // AE §6 STEP-4: the displayed adoption total must update after one adoption, with no reload. The
  // adopt response carries the new figure for the confirmation, but TrustZone's "Adopted N times"
  // line and `adoptedByMe` come from the LOADER — so the loader has to run again, or the page keeps
  // showing the pre-adoption number immediately above a confirmation stating the new one.
  const revalidator = useRevalidator();
  const [adopting, setAdopting] = useState(false);
  const [result, setResult] = useState<AdoptResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Shown for a repeat adopt (alreadyAdopted) OR a first adopt of an unsafe tool (safetyWarning) —
  // either reason routes through the same explicit-confirmation gate before doAdopt() is called.
  const [confirming, setConfirming] = useState(false);

  /**
   * The single adopt call, reused by the first-time button and the confirmed "adopt another copy".
   * The browser sends ONLY the target environment id — never a Dataverse URL. attemptConnectionBinding
   * is omitted (the experiment flag is off this stage). On success we auto-open Power Automate in a
   * new tab; the fallback link in AdoptedResult covers the case where the pop-up is blocked.
   */
  async function doAdopt() {
    setAdopting(true);
    setResult(null);
    setErrorMsg(null);

    try {
      const res = await adoptTool(toolId, env.id);

      if (res.kind === 'ok') {
        // A 200 does NOT mean adopted. The eligibility gate answers 200 with `adopted: false` and a
        // sentence (AE-F02), so this is checked BEFORE anything reads new_workflow_id — otherwise a
        // refusal would open a Power Automate deep link built from undefined ids for a flow that was
        // never created. Surfaced through errorMsg, the same place every other refusal appears; the
        // `finally` below still clears `adopting`, so the button recovers on its own.
        if (!res.body.adopted) {
          setErrorMsg(res.body.message);
          return;
        }
        setResult(res.body);

        // Re-run the loader so TrustZone's adoption total and `adoptedByMe` reflect this adoption
        // (AE §6 STEP-4). NOT awaited: the confirmation is already on screen from `setResult` above
        // and must not wait on a second round trip. The revalidate does not unmount this panel —
        // `env` still resolves — so `result` survives it and the confirmation stays put.
        revalidator.revalidate();

        // Auto-open the new flow in Power Automate. Composed client-side from the returned ids; the
        // backend never emits this host. noopener/noreferrer so the opened tab can't reach back into
        // this one. If the browser blocks the pop-up, window.open returns null — the fallback link in
        // AdoptedResult is the recovery path, so nothing more is needed here.
        //
        // SKIPPED in demo mode: no flow was created, so this would open Power Automate onto a
        // workflow id that does not exist. AdoptedResult says so instead of handing over a dead link.
        if (!res.body.demoMode) {
          const deepLink = powerAutomateDeepLink(res.body.environment_id, res.body.new_workflow_id);
          window.open(deepLink, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      if (res.kind === 'unauthorized') {
        setErrorMsg('Your session has expired. Sign in again and retry.');
        return;
      }
      if (res.status === 409) {
        setErrorMsg(
          'Sign out and sign back in so the Dataverse permission is granted, then retry.',
        );
        return;
      }
      if (res.status === 422 && res.code === 'clone_no_stored_clientdata') {
        setErrorMsg('This tool has no stored blueprint to adopt from.');
        return;
      }
      if (res.status === 404) {
        setErrorMsg('This tool no longer exists.');
        return;
      }
      setErrorMsg(`Adopt failed (${res.status}${res.code ? `: ${res.code}` : ''}).`);
    } catch {
      // A thrown network error (e.g. the API briefly unreachable) — surfaced the same way as a normal
      // failure response, and `finally` below still resets `adopting` so the button never gets stuck.
      setErrorMsg('Could not reach the server. Check your connection and try again.');
    } finally {
      setAdopting(false);
      setConfirming(false);
    }
  }

  /**
   * The button click. Adopt straight away UNLESS a confirmation is required first: already adopted
   * (repeat-copy confirmation), or TD-F15's safety gate (broken / open issue) — which applies even on
   * a first adopt, not only a repeat one.
   */
  function onAdoptClick() {
    // Blocked tools never reach the backend or the confirmation dialog. The button is already
    // disabled; this is the second line of defence, so a stray programmatic click cannot open a
    // confirm dialog for something that would be refused anyway.
    if (blocked) return;
    if (alreadyAdopted || safetyWarning) {
      setConfirming(true);
      return;
    }
    void doAdopt();
  }

  return (
    <div>
      {/* Why the control below is dead, ABOVE the control — a disabled button with no explanation is
          the failure this row exists to fix. role="status" not "alert": this is the tool's standing
          state on arrival, not something that just went wrong. */}
      {blocked && (
        <div className="tb-note tb-note--warn" role="status" style={{ maxWidth: '60ch' }}>
          {blocked.message}
        </div>
      )}
      <label className="tb-field" style={{ maxWidth: '30rem' }}>
        <span>Target environment</span>
        {/* One approved environment this stage. A real <select> so the control is honest about what
            it does, even with a single option. Disabled because there is nothing to change; the id
            sent on adopt comes from env.id directly (resolved from /api/flows/mine), not this field. */}
        <select value={env.id} disabled aria-describedby="env-hint">
          <option value={env.id}>{env.name}</option>
        </select>
      </label>

      {/* The viewer has adopted this tool before: say so plainly before offering another copy. */}
      {alreadyAdopted && (
        <p className="tb-note" role="status" style={{ maxWidth: '60ch' }}>
          You have already adopted this tool.
        </p>
      )}

      {errorMsg && (
        <div className="tb-note tb-note--warn" role="alert">
          {errorMsg}
        </div>
      )}

      {/* Confirmation gate — reached from either the already-adopted path or TD-F15's safety gate
          (broken / open issue on a FIRST adopt), and only after an explicit click. "Yes" calls the
          backend; "No"/"Cancel" closes the panel and does nothing. */}
      {confirming ? (
        <div className="tb-note tb-note--warn" role="group" aria-label="Confirm adopting this tool">
          {safetyWarning && (
            <p style={{ marginTop: 0 }}>
              <strong>{safetyWarning}</strong> Are you sure you want to adopt it?
            </p>
          )}
          {alreadyAdopted && (
            <p style={{ marginTop: safetyWarning ? undefined : 0 }}>
              Do you want to create another copy in Power Automate?
            </p>
          )}
          <button type="button" onClick={() => setConfirming(false)} disabled={adopting}>
            {alreadyAdopted ? 'No, keep existing' : 'Cancel'}
          </button>{' '}
          <button type="button" onClick={() => void doAdopt()} disabled={adopting}>
            {adopting
              ? 'Adopting…'
              : alreadyAdopted
                ? 'Yes, adopt another copy'
                : 'Yes, adopt anyway'}
          </button>
        </div>
      ) : (
        <button type="button" onClick={onAdoptClick} disabled={adopting || blocked !== null}>
          {adopting ? 'Adopting…' : alreadyAdopted ? 'Adopt another copy' : 'Adopt this tool'}
        </button>
      )}

      {/* The success summary + fallback link. Rendered under the controls so a repeat adopt from the
          already-adopted state still shows its result without unmounting the button. */}
      {result && (
        <div style={{ marginTop: '1rem' }}>
          <AdoptedResult result={result} />
        </div>
      )}
    </div>
  );
}

/**
 * AE-F04's other two handovers, plus the honest refusal for the third.
 *
 * WHY THIS IS A SEPARATE COMPONENT FROM AdoptPanel and not a mode inside it: they have nothing in
 * common. AdoptPanel is a stateful POST with an environment selector, a confirmation gate, a revalidate
 * and an error taxonomy. Copying a prompt writes nothing to the server and needs none of that — folding
 * it in would mean every branch of that component asking "but is this actually an adopt?".
 *
 * WHICH MODE IS SHOWN IS THE API'S DECISION (`db/handover.ts`), never re-derived here. The adopt
 * endpoint refuses to clone a prompt or a link, so a page that guessed differently would offer a
 * handover the server rejects. `handover.unavailable` is rendered in preference to any control, for the
 * same reason a blocked Adopt button explains itself: a dead control with no reason is the failure.
 *
 * `declaredTypeMismatch` is deliberately NOT shown. It reports that `app.tool.artefact_type` disagrees
 * with what is stored, which today it always does — both tool-creation paths write the constant 'link'.
 * That is a fact about a column, useful in the API response for diagnosis, and noise on an adopter's
 * screen.
 */
function HandoverPanel({ handover, builderName }: { handover: Handover; builderName: string }) {
  if (handover.unavailable) {
    return (
      <div className="tb-note tb-note--warn" role="status" style={{ maxWidth: '60ch' }}>
        {handover.unavailable}
        {handover.filename && (
          <>
            {' '}
            The file is <span className="tb-mono">{handover.filename}</span>
            {handover.byteSize !== null ? ` (${formatBytes(handover.byteSize)})` : ''}.
          </>
        )}
      </div>
    );
  }
  if (handover.mode === 'prompt' && handover.promptText !== null) {
    return <PromptHandover promptText={handover.promptText} builderName={builderName} />;
  }
  if (handover.mode === 'link' && handover.linkUrl !== null) {
    return <LinkHandover linkUrl={handover.linkUrl} />;
  }
  // Unreachable: every mode above either carries its payload or carries `unavailable`. Rendering a
  // sentence rather than nothing, because a silently empty handover section is the one outcome that
  // leaves an adopter with no idea what to do next.
  return (
    <p className="tb-muted" style={{ maxWidth: '60ch' }}>
      This tool cannot be handed over automatically. Ask {builderName} for a copy.
    </p>
  );
}

/** 250 MB is the cap (UP-F11), so KB/MB is the whole range worth writing. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * AE-F04: a prompt is "copied in ONE ACTION". One button, one click, no select-all-then-Ctrl-C — and
 * the text is on screen as well, because a copy that silently failed and a copy that worked look
 * identical from the outside.
 *
 * The clipboard write can fail for reasons the page cannot fix (an insecure context, a browser policy,
 * a denied permission). That is why `navigator.clipboard` is feature-tested rather than assumed, and
 * why failure falls back to "select the text below" instead of an apology: the prompt is already
 * rendered in a textarea the person can select, so the handover still completes by hand.
 */
function PromptHandover({ promptText, builderName }: { promptText: string; builderName: string }) {
  const [copied, setCopied] = useState<'idle' | 'ok' | 'failed'>('idle');

  async function copy() {
    try {
      if (!navigator.clipboard) throw new Error('no clipboard api');
      await navigator.clipboard.writeText(promptText);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  }

  return (
    <div>
      <p className="tb-muted" style={{ maxWidth: '60ch' }}>
        This tool is a prompt written by {builderName}. Copy it and paste it into whichever
        assistant you use — nothing is created in Power Automate and nothing is installed.
      </p>
      <label className="tb-field">
        <span>The prompt</span>
        <textarea readOnly value={promptText} rows={8} style={{ fontFamily: 'inherit' }} />
      </label>
      <button type="button" onClick={() => void copy()}>
        Copy the prompt
      </button>{' '}
      {copied === 'ok' && (
        <span className="tb-note tb-note--ok" role="status">
          Copied to your clipboard.
        </span>
      )}
      {copied === 'failed' && (
        <span className="tb-note tb-note--warn" role="status">
          Your browser would not let the page use the clipboard. Select the text above and copy it.
        </span>
      )}
    </div>
  );
}

/**
 * AE-F04: a link is "opened AND MARKED AS LEAVING THE PLATFORM". The marking is the requirement, not
 * the opening — so the destination host is shown BEFORE the control, in words, rather than hidden
 * behind link text the person only sees in a status bar.
 *
 * `rel="noreferrer noopener"` on a `target="_blank"` link to somewhere outside the platform: noopener
 * so the opened page cannot reach back into this one through `window.opener`, noreferrer so it is not
 * told which internal tool page sent the person. The URL is always `https://` — `0005`'s CHECK on
 * `link_url` enforces that in the database, so there is no scheme to validate here.
 */
function LinkHandover({ linkUrl }: { linkUrl: string }) {
  const host = linkHost(linkUrl);
  return (
    <div>
      <div className="tb-note tb-note--warn" role="status" style={{ maxWidth: '60ch' }}>
        This tool lives outside Trailblazer. Opening it takes you to{' '}
        <strong>{host ?? 'another site'}</strong> in a new tab, and what happens there is not
        covered by this platform&rsquo;s reviews or trust signals.
      </div>
      <p className="tb-mono" style={{ overflowWrap: 'anywhere', maxWidth: '60ch' }}>
        {linkUrl}
      </p>
      <a href={linkUrl} target="_blank" rel="noreferrer noopener">
        Open in a new tab
      </a>
    </div>
  );
}

/**
 * The ACT heading. It used to read "Adopt this tool" over every tool in the catalogue, which is a
 * promise about Power Automate that only the clone handover keeps — so the heading is part of the
 * handover, not a constant above it.
 */
function handoverHeading(mode: HandoverMode): string {
  switch (mode) {
    case 'prompt':
      return 'Copy this prompt';
    case 'link':
      return 'Open this tool';
    case 'file':
      return 'Get this file';
    default:
      return 'Adopt this tool';
  }
}

/** The destination host, for the leaving-the-platform notice. Null when the URL will not parse. */
function linkHost(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

// ── Learn from others: comments, issues, did-it-work (FB-F05..F18, TD-F19..F21) ──────────────────

function CommentItem({
  toolId,
  node,
  toolMine,
  onChanged,
}: {
  toolId: string;
  node: CommentNode;
  toolMine: boolean;
  onChanged: (updated: Partial<CommentNode> & { commentId: string }) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.body);
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState(false);

  async function saveEdit() {
    setBusy(true);
    try {
      const res = await editComment(toolId, node.commentId, draft);
      if (res.kind === 'ok') {
        onChanged({ commentId: node.commentId, body: draft, edited: true });
        setEditing(false);
      }
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      const res = await deleteComment(toolId, node.commentId);
      if (res.kind === 'ok') {
        onChanged({
          commentId: node.commentId,
          removed: true,
          body: '[removed]',
          authorName: null,
        });
      }
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove() {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const res = await removeCommentByBuilder(toolId, node.commentId, reason.trim());
      if (res.kind === 'ok') {
        onChanged({
          commentId: node.commentId,
          removed: true,
          body: '[removed]',
          authorName: null,
        });
        setRemoving(false);
      }
    } finally {
      setBusy(false);
    }
  }

  /** FB-F17: flag this comment for a moderator to review. Never removes anything by itself. */
  async function doReport() {
    setBusy(true);
    try {
      const res = await reportComment(toolId, node.commentId);
      if (res.kind === 'ok') setReported(true);
    } finally {
      setBusy(false);
    }
  }

  if (node.removed) {
    return (
      <li>
        <span className="tb-muted">[removed]</span>
      </li>
    );
  }

  return (
    <li style={{ marginBottom: '0.5rem' }}>
      <div>
        <strong>{node.authorName ?? 'Former colleague'}</strong>{' '}
        <span className="tb-muted">
          {shortDateTime(node.createdAt)}
          {node.edited ? ' (edited)' : ''}
        </span>
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ width: '100%' }}
          />
          <button
            type="button"
            onClick={() => void saveEdit()}
            disabled={busy || draft.trim().length === 0}
          >
            Save
          </button>{' '}
          <button type="button" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      ) : (
        <p style={{ margin: '0.25rem 0' }}>{node.body}</p>
      )}
      {!editing && node.isOwn && (
        <button type="button" onClick={() => setEditing(true)} disabled={busy}>
          Edit
        </button>
      )}{' '}
      {!editing && node.isOwn && (
        <button type="button" onClick={() => void doDelete()} disabled={busy}>
          Delete
        </button>
      )}
      {!editing && !node.isOwn && toolMine && !removing && (
        <button type="button" onClick={() => setRemoving(true)} disabled={busy}>
          Remove
        </button>
      )}
      {!editing && !node.isOwn && (
        <button type="button" onClick={() => void doReport()} disabled={busy || reported}>
          {reported ? 'Reported' : 'Report'}
        </button>
      )}
      {removing && (
        <div className="tb-note tb-note--warn" role="group" aria-label="Remove this comment">
          <label className="tb-field">
            <span>Reason (shown to the person who wrote it)</span>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <button
            type="button"
            onClick={() => void confirmRemove()}
            disabled={busy || reason.trim().length < 3}
          >
            Confirm remove
          </button>{' '}
          <button type="button" onClick={() => setRemoving(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      )}
      {node.replies.length > 0 && (
        <ul
          style={{
            marginTop: '0.5rem',
            paddingLeft: '1.5rem',
            borderLeft: '2px solid var(--tb-line)',
          }}
        >
          {node.replies.map((reply) => (
            <CommentItem
              key={reply.commentId}
              toolId={toolId}
              node={reply}
              toolMine={toolMine}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function Comments({
  toolId,
  toolMine,
  initial,
}: {
  toolId: string;
  toolMine: boolean;
  initial: CommentsPage;
}) {
  const [comments, setComments] = useState<CommentNode[]>(initial.comments);
  const [total, setTotal] = useState(initial.total);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  function applyChange(updated: Partial<CommentNode> & { commentId: string }) {
    setComments((prev) => prev.map((c) => applyToNode(c, updated)));
  }
  function applyToNode(
    node: CommentNode,
    updated: Partial<CommentNode> & { commentId: string },
  ): CommentNode {
    if (node.commentId === updated.commentId) return { ...node, ...updated };
    return { ...node, replies: node.replies.map((r) => applyToNode(r, updated)) };
  }

  async function submit() {
    if (draft.trim().length === 0) return;
    setPosting(true);
    try {
      const res = await postComment(toolId, draft.trim());
      if (res.kind !== 'ok') return;
      setDraft('');
      setNudge(res.body.looksLikeFaultReport);
      // Re-fetch the first page so ordering stays server-truthful rather than guessing where the new
      // comment lands relative to any other activity since the page loaded.
      const refreshed = await fetchComments(toolId);
      if (refreshed.kind === 'ok') {
        setComments(refreshed.body.comments);
        setTotal(refreshed.body.total);
      }
    } finally {
      setPosting(false);
    }
  }

  async function loadMore() {
    setLoadingMore(true);
    try {
      const res = await fetchComments(toolId, { offset: comments.length });
      if (res.kind === 'ok') {
        setComments((prev) => [...prev, ...res.body.comments]);
      }
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section aria-labelledby="comments-heading">
      <h2 id="comments-heading">Comments</h2>
      <p className="tb-muted" style={{ maxWidth: '60ch' }}>
        Reuse notes, tips and questions — posted under your real name.
      </p>

      <div>
        <label className="tb-field">
          <span>Add a comment</span>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={2000} />
        </label>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={posting || draft.trim().length === 0}
        >
          {posting ? 'Posting…' : 'Post comment'}
        </button>
      </div>

      {nudge && (
        <div className="tb-note tb-note--warn" role="status">
          This sounds like it might be a fault report.{' '}
          <button type="button" onClick={() => setNudge(false)}>
            Use &ldquo;Report an issue&rdquo; below instead
          </button>
        </div>
      )}

      {comments.length === 0 ? (
        <p className="tb-muted">No comments yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
          {comments.map((c) => (
            <CommentItem
              key={c.commentId}
              toolId={toolId}
              node={c}
              toolMine={toolMine}
              onChanged={applyChange}
            />
          ))}
        </ul>
      )}

      {comments.length < total && (
        <button type="button" onClick={() => void loadMore()} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : `Load more (${total - comments.length} more)`}
        </button>
      )}
    </section>
  );
}

function ReportIssueForm({
  toolId,
  prefill,
}: {
  toolId: string;
  prefill: { kind: 'broken' | 'security' | 'sensitive_data'; body: string } | null;
}) {
  const [kind, setKind] = useState<'broken' | 'security' | 'sensitive_data'>(
    prefill?.kind ?? 'broken',
  );
  const [body, setBody] = useState(prefill?.body ?? '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit() {
    if (body.trim().length < 10) return;
    setSending(true);
    try {
      const res = await postIssueReport(toolId, kind, body.trim());
      if (res.kind === 'ok') {
        setSent(true);
        setBody('');
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <section aria-labelledby="report-heading">
      <h2 id="report-heading">Report an issue</h2>
      <p className="tb-muted" style={{ maxWidth: '60ch' }}>
        Private to the platform owner and the SWAT team. Reporting never removes anything by itself
        — a person reviews and resolves it.
      </p>
      {sent && (
        <p className="tb-note tb-note--ok" role="status">
          Thank you — your report has been sent.
        </p>
      )}
      <label className="tb-field" style={{ maxWidth: '20rem' }}>
        <span>What kind of issue?</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="broken">Broken</option>
          <option value="security">Security concern</option>
          <option value="sensitive_data">Sensitive data concern</option>
        </select>
      </label>
      <label className="tb-field">
        <span>What happened?</span>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} />
      </label>
      <button
        type="button"
        onClick={() => void submit()}
        disabled={sending || body.trim().length < 10}
      >
        {sending ? 'Sending…' : 'Send report'}
      </button>
    </section>
  );
}

function OpenIssueResolve({
  toolId,
  issueId,
  canResolve,
}: {
  toolId: string;
  issueId: string;
  canResolve: boolean;
}) {
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!canResolve || done) return done ? <span className="tb-muted"> — resolved</span> : null;

  async function confirm() {
    if (note.trim().length < 10) return;
    setBusy(true);
    try {
      const res = await resolveIssueReport(toolId, issueId, note.trim());
      if (res.kind === 'ok') setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return resolving ? (
    <span>
      {' '}
      <input
        type="text"
        placeholder="Resolution note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button
        type="button"
        onClick={() => void confirm()}
        disabled={busy || note.trim().length < 10}
      >
        Confirm resolved
      </button>
    </span>
  ) : (
    <button type="button" onClick={() => setResolving(true)}>
      Mark resolved
    </button>
  );
}

function DidItWorkBanner({ toolId }: { toolId: string }) {
  const [visible, setVisible] = useState(true);
  const [prefill, setPrefill] = useState<{ kind: 'broken'; body: string } | null>(null);

  async function answer(value: boolean) {
    try {
      const res = await postDidItWork(toolId, value);
      if (res.kind === 'ok' && res.body.reportPrefill) setPrefill(res.body.reportPrefill);
    } finally {
      setVisible(false);
    }
  }

  return (
    <>
      {visible && (
        <div className="tb-note" role="status" style={{ maxWidth: '60ch' }}>
          <p style={{ marginTop: 0 }}>Did this tool work for you?</p>
          <button type="button" onClick={() => void answer(true)}>
            Yes
          </button>{' '}
          <button type="button" onClick={() => void answer(false)}>
            No
          </button>{' '}
          <button type="button" onClick={() => setVisible(false)}>
            Dismiss
          </button>
        </div>
      )}
      {prefill && <ReportIssueForm toolId={toolId} prefill={prefill} />}
    </>
  );
}

// ── Owner actions (TD-F23, STEP-6) — reachable ONLY for PlatformOwner ─────────────────────────────

/** Set health, edit category/tags: PlatformOwner (health is also SWATMember's per CAT-F20, but the
 *  panel here is PlatformOwner-gated as a whole — SWAT has no reason to be on this page's owner
 *  panel; they act from the admin Tool Governance list instead). */
function HealthAndClassificationForm({
  tool,
  onChanged,
}: {
  tool: CatalogueToolDetail;
  onChanged: () => void;
}) {
  const [refData, setRefData] = useState<ToolRefData | null>(null);
  // The backend guarantees healthState is one of the three states; narrowed here since the
  // frontend's CatalogueToolDetail types it as a plain string (it is read-only everywhere else).
  const [health, setHealth] = useState(tool.healthState as 'Healthy' | 'Unmaintained' | 'Broken');
  const [categoryId, setCategoryId] = useState(tool.categoryId);
  /**
   * UP-F55, and the reason the access requirement is edited HERE rather than on its own control: it is
   * the same shape of decision as the primary category (one governed value, one dropdown, PlatformOwner
   * only) and it lapses the review marker for the same reason. Until now nothing wrote it after upload,
   * so a tool's audience was fixed at creation and the only way to restrict one was to delete and
   * re-upload it.
   */
  const [accessRequirementId, setAccessRequirementId] = useState(tool.accessRequirementId);
  const [tags, setTags] = useState(tool.tags.join(', '));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void apiGet<ToolRefData>('/api/tools/refdata').then((res) => {
      if (res.kind === 'ok') setRefData(res.body);
    });
  }, []);

  async function saveHealth() {
    setBusy(true);
    setMessage(null);
    try {
      const res = await setToolHealth(tool.toolId, health);
      setMessage(
        res.kind === 'ok'
          ? 'Health updated.'
          : `Failed (${res.kind === 'error' ? res.status : 'unauthorized'}).`,
      );
      if (res.kind === 'ok') onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function saveClassification() {
    setBusy(true);
    setMessage(null);
    try {
      const tagList = tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      const res = await editToolClassification(tool.toolId, {
        categoryId,
        accessRequirementId,
        tags: tagList,
      });
      setMessage(
        res.kind === 'ok'
          ? 'Classification updated.'
          : res.kind === 'error' && res.status === 400
            ? 'Failed — that access requirement is unknown or retired.'
            : `Failed (${res.kind === 'error' ? res.status : 'unauthorized'}).`,
      );
      if (res.kind === 'ok') onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label className="tb-field" style={{ maxWidth: '20rem' }}>
        <span>Health state</span>
        <select value={health} onChange={(e) => setHealth(e.target.value as typeof health)}>
          <option value="Healthy">Healthy</option>
          <option value="Unmaintained">Unmaintained</option>
          <option value="Broken">Broken</option>
        </select>
      </label>
      <button type="button" onClick={() => void saveHealth()} disabled={busy}>
        Save health state
      </button>

      <label className="tb-field" style={{ maxWidth: '20rem' }}>
        <span>Primary category</span>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(Number(e.target.value))}
          disabled={!refData}
        >
          {(refData?.categories ?? [{ id: categoryId, label: '…' }]).map((c: RefOption) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <label className="tb-field" style={{ maxWidth: '20rem' }}>
        <span>Who may see it</span>
        <select
          value={accessRequirementId}
          onChange={(e) => setAccessRequirementId(Number(e.target.value))}
          disabled={!refData}
        >
          {/* The tool's CURRENT value is added when refdata does not offer it, which happens whenever
              it has been retired since the tool was filed under it. Without this the dropdown would
              silently show a different requirement as selected, and saving would move the tool onto
              that one. */}
          {(refData?.accessRequirements ?? []).some(
            (a: RefOption) => a.id === accessRequirementId,
          ) ? null : (
            <option value={accessRequirementId}>{tool.accessRequirementLabel} (retired)</option>
          )}
          {(refData?.accessRequirements ?? []).map((a: RefOption) => (
            <option key={a.id} value={a.id}>
              {a.label}
            </option>
          ))}
        </select>
      </label>
      <label className="tb-field">
        <span>Tags (comma-separated, up to 5)</span>
        <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} />
      </label>
      <button type="button" onClick={() => void saveClassification()} disabled={busy}>
        Save classification
      </button>
      {message && (
        <p className="tb-note" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

/** Withdraw/restore: the tool's own builder, Reviewer, or PlatformOwner. Here we only reach this
 *  panel as PlatformOwner, but the backend enforces the full builder-or-role gate regardless. */
function WithdrawRestoreControl({
  tool,
  onChanged,
}: {
  tool: CatalogueToolDetail;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const isWithdrawn = tool.publicationStatus === 'Withdrawn';

  async function doWithdraw() {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const res = await withdrawTool(tool.toolId, reason.trim());
      setMessage(
        res.kind === 'ok'
          ? 'Withdrawn.'
          : `Failed (${res.kind === 'error' ? res.status : 'unauthorized'}).`,
      );
      if (res.kind === 'ok') {
        setConfirming(false);
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  }

  async function doRestore() {
    setBusy(true);
    try {
      const res = await restoreTool(tool.toolId);
      setMessage(
        res.kind === 'ok'
          ? 'Restored.'
          : `Failed (${res.kind === 'error' ? res.status : 'unauthorized'}).`,
      );
      if (res.kind === 'ok') onChanged();
    } finally {
      setBusy(false);
    }
  }

  if (isWithdrawn) {
    return (
      <div>
        <p className="tb-muted">This tool is currently withdrawn.</p>
        <button type="button" onClick={() => void doRestore()} disabled={busy}>
          Restore
        </button>
        {message && (
          <p className="tb-note" role="status">
            {message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      {confirming ? (
        <div className="tb-note tb-note--warn" role="group" aria-label="Confirm withdraw">
          <label className="tb-field">
            <span>Reason</span>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <button
            type="button"
            onClick={() => void doWithdraw()}
            disabled={busy || reason.trim().length < 3}
          >
            Confirm withdraw
          </button>{' '}
          <button type="button" onClick={() => setConfirming(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirming(true)}>
          Withdraw this tool
        </button>
      )}
      {message && (
        <p className="tb-note" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

/** Merge a duplicate: PlatformOwner only. Soft pointer — the losing record is retained for audit,
 *  never deleted (CAT-F26). */
function MergeControl({ tool, onChanged }: { tool: CatalogueToolDetail; onChanged: () => void }) {
  const [candidates, setCandidates] = useState<AdminDuplicateCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function search() {
    setBusy(true);
    try {
      const res = await fetchAdminDuplicateCandidates(tool.toolId);
      setCandidates(res.kind === 'ok' ? res.body.candidates : []);
    } finally {
      setBusy(false);
    }
  }

  async function mergeInto(otherToolId: string) {
    setBusy(true);
    try {
      const res = await mergeTools(otherToolId, tool.toolId);
      setMessage(
        res.kind === 'ok'
          ? 'Merged — this tool now points to the surviving record.'
          : `Failed (${res.kind === 'error' ? res.status : 'unauthorized'}).`,
      );
      if (res.kind === 'ok') onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button type="button" onClick={() => void search()} disabled={busy}>
        Find duplicate candidates
      </button>
      {candidates && candidates.length === 0 && <p className="tb-muted">No candidates found.</p>}
      {candidates && candidates.length > 0 && (
        <ul>
          {candidates.map((c) => (
            <li key={c.toolId}>
              {c.name} — by {c.ownerName} ({c.publicationStatus}){' '}
              <button type="button" onClick={() => void mergeInto(c.toolId)} disabled={busy}>
                Merge this tool into {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {message && (
        <p className="tb-note" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

/** Record a new owner for an ownerless tool: PlatformOwner or SWATMember. Person lookup reuses the
 *  global search field's own suggest endpoint. Only rendered when the tool is actually ownerless. */
function ReassignOwnerControl({
  tool,
  onChanged,
}: {
  tool: CatalogueToolDetail;
  onChanged: () => void;
}) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<{ userId: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function search(q: string) {
    setQuery(q);
    if (q.trim().length < 2) {
      setMatches([]);
      return;
    }
    const { people } = await fetchSuggestions(q);
    setMatches(people.map((p) => ({ userId: p.userId, name: p.name })));
  }

  async function reassign(userId: string) {
    setBusy(true);
    try {
      const res = await reassignToolOwner(tool.toolId, userId);
      setMessage(
        res.kind === 'ok'
          ? 'Owner reassigned.'
          : `Failed (${res.kind === 'error' ? res.status : 'unauthorized'}).`,
      );
      if (res.kind === 'ok') onChanged();
    } finally {
      setBusy(false);
    }
  }

  if (!tool.ownerlessFlag) return null;

  return (
    <div>
      <p className="tb-note tb-note--warn" role="note">
        This tool is ownerless.
      </p>
      <input
        type="text"
        value={query}
        placeholder="Search for a new owner…"
        onChange={(e) => void search(e.target.value)}
      />
      {matches.length > 0 && (
        <ul>
          {matches.map((m) => (
            <li key={m.userId}>
              {m.name}{' '}
              <button type="button" onClick={() => void reassign(m.userId)} disabled={busy}>
                Make owner
              </button>
            </li>
          ))}
        </ul>
      )}
      {message && (
        <p className="tb-note" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

/** CAT-F30: the change history for this tool — who changed what, from what to what, and why.
 *
 *  Read-only, and deliberately the LAST block in the panel: it is the record of the actions above it.
 *  Loaded on demand rather than with the page, because every viewer of a tool page would otherwise
 *  pay for a governance read that only the platform owner can see.
 *
 *  A tool with no history renders an explicit "nothing recorded yet" rather than an empty box —
 *  before this branch NOTHING was ever written to app.tool_audit, so on the live database every tool
 *  starts here, and a blank panel would read as a failed fetch. */
function ChangeHistory({ tool }: { tool: CatalogueToolDetail }) {
  const [entries, setEntries] = useState<ToolAuditEntry[] | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');

  async function load() {
    setState('loading');
    const res = await fetchToolAudit(tool.toolId);
    if (res.kind === 'ok') {
      setEntries(res.body.entries);
      setState('idle');
    } else {
      setState('failed');
    }
  }

  return (
    <div>
      <h3>Change history</h3>
      {entries === null ? (
        <>
          <button type="button" onClick={() => void load()} disabled={state === 'loading'}>
            {state === 'loading' ? 'Loading…' : 'Show change history'}
          </button>
          {state === 'failed' && (
            <p className="tb-note" role="status">
              Could not load the history.
            </p>
          )}
        </>
      ) : entries.length === 0 ? (
        <p className="tb-muted">
          Nothing recorded yet. Governance changes made from this panel are recorded here from now
          on.
        </p>
      ) : (
        <ol>
          {entries.map((e) => (
            <li key={e.auditId}>
              <strong>{FIELD_LABELS[e.field] ?? e.field}</strong>{' '}
              {e.oldValue === null ? 'set to' : 'changed from'}{' '}
              {e.oldValue !== null && <em>{e.oldValue}</em>}
              {e.oldValue !== null && ' to '}
              <em>{e.newValue ?? 'nothing'}</em>
              {' — '}
              {e.actorKind === 'job' ? 'automatic sweep' : (e.actorName ?? 'a former colleague')}
              {', '}
              <time dateTime={e.changedAt}>{new Date(e.changedAt).toLocaleString()}</time>
              {e.reason && (
                <>
                  {'. '}
                  <span className="tb-muted">{e.reason}</span>
                </>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** The audit column stores the DB field name; these are the human labels. An unmapped field falls
 *  back to its raw name rather than being hidden — a trail that silently drops rows it does not
 *  recognise is worse than one that shows a slightly technical word. */
const FIELD_LABELS: Record<string, string> = {
  publication_status: 'Publication status',
  review_marker: 'Review marker',
  health_state: 'Health state',
  primary_category_id: 'Primary category',
  access_requirement_id: 'Access requirement',
  tags: 'Tags',
  withdrawn_at: 'Withdrawn',
  archived_at: 'Archived',
  ownerless_flag: 'Ownerless',
  builder_user_id: 'Owner',
  merged_into_tool_id: 'Merged into',
};

/** TD-F23 / STEP-6: every catalogue governance action, from the same page, PlatformOwner only. */
function OwnerActionsPanel({ tool }: { tool: CatalogueToolDetail }) {
  const revalidator = useRevalidator();
  const onChanged = () => revalidator.revalidate();

  return (
    <section aria-labelledby="owner-actions-heading">
      <h2 id="owner-actions-heading">Owner actions</h2>
      <p className="tb-muted" style={{ maxWidth: '60ch' }}>
        Visible to you only, as the platform owner. Each action here is the SAME one reachable from
        the admin Tool Governance list — not a second implementation.
      </p>
      <HealthAndClassificationForm tool={tool} onChanged={onChanged} />
      <WithdrawRestoreControl tool={tool} onChanged={onChanged} />
      <MergeControl tool={tool} onChanged={onChanged} />
      <ReassignOwnerControl tool={tool} onChanged={onChanged} />
      <ChangeHistory tool={tool} />
    </section>
  );
}

export function ToolDetail() {
  const data = useLoaderData() as DetailLoaderData;
  const [ratingState, setRatingState] = useState<{
    average: number | null;
    count: number;
    mine: number | null;
  } | null>(null);

  // Record this view into the per-browser "recently clicked tools" list, which the search
  // dropdown's empty state reads. This is BROWSING history (a page the user opened), not search
  // history — AE-F36/DS-F19 forbid the latter, and nothing here records what was searched. Placed
  // before the state guards below so the hook order stays stable; it fires only on an ok load.
  const okTool = data.state === 'ok' ? data.tool : null;
  useEffect(() => {
    if (okTool) recordToolClick(okTool.toolId, okTool.name);
  }, [okTool]);

  if (data.state === 'signedOut') return <SignedOut title="Tool" returnTo="/tools" />;
  if (data.state === 'featureDisabled') return <FeatureDisabled title="Tool" />;
  if (data.state === 'notFound') {
    return (
      <section aria-labelledby="notfound-heading" className="tb-tools">
        <style>{TOOLS_STYLES}</style>
        <h1 id="notfound-heading">Tool not found</h1>
        <p>
          <Link to="/tools">Back to the catalogue</Link>
        </p>
      </section>
    );
  }

  const { tool: loadedTool, comments, env, envNote, isPlatformOwner } = data;
  const tool = ratingState
    ? {
        ...loadedTool,
        ratingAverage: ratingState.average,
        ratingCount: ratingState.count,
        myRating: ratingState.mine,
      }
    : loadedTool;

  // AE-F04 — which handover the ACT section renders. Absent field reads as 'clone', so a response from
  // an API build without it behaves exactly as this page did before the field existed.
  const handoverMode: HandoverMode = tool.handover?.mode ?? 'clone';

  return (
    <section aria-labelledby="detail-heading" className="tb-tools">
      <style>{TOOLS_STYLES}</style>
      <h1 id="detail-heading">{tool.name}</h1>
      <p>
        <Link to="/tools">Back to the catalogue</Link>
      </p>

      {/* ── DECIDE ── */}
      <dl>
        <dt>Description</dt>
        <dd>{tool.description}</dd>
        <dt>Problem it solves</dt>
        <dd>{tool.problemSolved}</dd>
      </dl>
      <BuilderCredit tool={tool} />
      <dl>
        {/* TD-F04: the category and every tag are links into a catalogue view filtered to them, so the
            classification is a way onward rather than a label to read. Both hrefs come from
            `catalogueLinks.ts` — the same builders the result cards use, which is what stops this page
            and that one from spelling the same filter two different ways.

            "Applies to" stays plain text: it is the department the tool is FOR, which reads as
            provenance, and the catalogue offers it as a filter dropdown rather than a click-through. */}
        <dt>Category</dt>
        <dd>
          <Link to={catalogueCategoryHref(tool.categoryId)}>{tool.categoryLabel}</Link>
        </dd>
        <dt>Applies to</dt>
        <dd>{tool.departmentLabel}</dd>
        <dt>Tags</dt>
        <dd>
          {/* The em dash stays HERE, unlike the card's tag line: this is a <dd> answering a <dt> that
              is always rendered, so an empty one would leave "Tags" followed by nothing. */}
          {tool.tagLinks.length === 0
            ? '—'
            : tool.tagLinks.map((t, i) => (
                <span key={t.tagId}>
                  {i > 0 ? <span aria-hidden="true">, </span> : null}
                  <Link to={catalogueTagHref(t)}>{t.label}</Link>
                </span>
              ))}
        </dd>
      </dl>

      {/* ── TRUST ── */}
      <TrustZone
        tool={tool}
        onRated={(stars) =>
          setRatingState((prev) => ({
            average: prev?.average ?? tool.ratingAverage,
            count: prev?.count ?? tool.ratingCount,
            mine: stars,
          }))
        }
      />

      {tool.trustLabel === 'Unverified' && (
        <p className="tb-note tb-note--warn" role="note" style={{ maxWidth: '60ch' }}>
          Unverified tools are shared by colleagues but have not yet been reviewed. Connector
          credentials are not copied when adopting.
        </p>
      )}

      {/* ── ACT ── */}
      {/* AE-F04 — ONE handover, chosen by the API (`db/handover.ts`), not four sections with three of
          them empty. The heading changes with it: "Adopt this tool" is a promise about Power Automate,
          and it was previously shown over every tool including ones that are a prompt or a link.

          The mode falls back to 'clone' when the field is absent, which keeps an older API build (and
          any response predating this field) rendering exactly the page it rendered before. On current
          data the API returns 'clone' for every tool anyway — nothing writes `app.artefact` yet. */}
      <h2>{handoverHeading(handoverMode)}</h2>

      {/* TD-F15: a broken tool or one with an open issue is warned ABOVE the action. OUTSIDE the mode
          branch, deliberately — a prompt with an open issue against it is just as much a warning as a
          flow with one, and the confirmation gate inside AdoptPanel covers only the clone path. */}
      {safetyWarning(tool) && (
        <div className="tb-note tb-note--warn" role="alert">
          {safetyWarning(tool)}
        </div>
      )}

      {handoverMode === 'clone' ? (
        <>
          <p className="tb-muted" style={{ maxWidth: '60ch' }}>
            Adopting copies this flow's definition into your approved Power Automate environment as
            a new Draft flow. Connector credentials are never copied — you connect your own after
            adopting.
          </p>
          {/* The environment note belongs to THIS handover only: it is about Power Automate, and there
              is no environment to resolve for a prompt or a link. */}
          {envNote && (
            <div className="tb-note tb-note--warn" role="alert">
              {envNote}
            </div>
          )}
          {env ? (
            <AdoptPanel
              toolId={tool.toolId}
              env={env}
              alreadyAdopted={tool.adoptedByMe}
              safetyWarning={safetyWarning(tool)}
              blocked={tool.adoptBlockedReason ?? null}
            />
          ) : (
            !envNote && <p className="tb-muted">Adoption is unavailable right now.</p>
          )}
        </>
      ) : (
        <HandoverPanel handover={tool.handover!} builderName={tool.ownerName} />
      )}
      <LikeSaveActions tool={tool} />

      {/* AE-F05 / TD-F17: setup instructions and dependencies belong on the SAME screen at the same
          moment as the handover, not on a page the adopter has to go and find afterwards. They sit at the
          TOP of this list, above the provenance rows, because they are the two things an adopter acts on
          and Source/Blueprint stored are the two they merely check. Both are required at upload (NOT NULL
          since 0004), so neither needs an absence string — `dependencies` reads "none" when the builder
          said so, which is an answer rather than a blank. */}
      <dl>
        <dt>How to set it up</dt>
        <dd style={{ whiteSpace: 'pre-wrap' }}>{tool.setupInstructions}</dd>
        <dt>Dependencies</dt>
        <dd style={{ whiteSpace: 'pre-wrap' }}>{tool.dependencies}</dd>
        <dt>Source</dt>
        <dd>{tool.externalSource ?? 'Unknown'}</dd>
        <dt>Source flow name</dt>
        <dd>{tool.sourceName ?? '—'}</dd>
        <dt>Blueprint stored</dt>
        <dd>{tool.hasClientdata ? 'Yes' : 'No'}</dd>
        <dt>Connectors required</dt>
        <dd>{connectorList(tool.requiredConnectors)}</dd>
        {tool.connectorSetupNotes && (
          <>
            <dt>Requirements</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{tool.connectorSetupNotes}</dd>
          </>
        )}
      </dl>

      {/* The builder's mandatory per-step setup descriptions, surfaced read-only for adopters. */}
      {tool.setupSteps && tool.setupSteps.length > 0 && (
        <ConnectorDescriptions steps={tool.setupSteps} />
      )}

      {/* ── LEARN FROM OTHERS ── */}
      {tool.showDidItWorkPrompt && <DidItWorkBanner toolId={tool.toolId} />}
      <Comments toolId={tool.toolId} toolMine={tool.mine} initial={comments} />
      {!tool.showDidItWorkPrompt && <ReportIssueForm toolId={tool.toolId} prefill={null} />}
      {/* Not gated on the count: this is the RESOLVE affordance, and a viewer who may not read a
          report cannot resolve one either. `visibility` is the explicit test rather than relying on the
          list being empty, so a future change to either cannot silently offer resolution without a body. */}
      {tool.openIssuesVisibility === 'full' && tool.openIssues.length > 0 && (
        <section aria-labelledby="resolve-heading">
          <h2 id="resolve-heading">Resolve an issue</h2>
          <ul>
            {tool.openIssues.map((i) => (
              <li key={i.issueId}>
                {i.kind}: {i.body}
                <OpenIssueResolve toolId={tool.toolId} issueId={i.issueId} canResolve={tool.mine} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── OWNER ONLY (STEP-6, TD-F23) ── */}
      {isPlatformOwner && <OwnerActionsPanel tool={tool} />}
    </section>
  );
}
